/**
 * @fileoverview Funciones de separación de coordenadas concatenadas PTEL
 * @module lib/coordinate/separators
 * 
 * Extraído de coordinateNormalizer.ts como parte de TD-002.B
 * Contiene funciones para separar coordenadas fusionadas o concatenadas
 * en un solo valor numérico.
 * 
 * @version 1.0.0
 * @date 2026-01-25
 */

import type { ResultadoConcatenacion, NivelConfianza } from './types';
import { RANGOS_ANDALUCIA } from './constants';

// ============================================================================
// TIPOS ESPECÍFICOS DE SEPARADORES
// ============================================================================

/**
 * Resultado de separación de coordenadas pegadas (13-14 dígitos sin separadores)
 * Formato BE-01: Y+X fusionadas donde Y empieza por 4 (característico Andalucía)
 */
export interface ResultadoCoordenadasPegadas {
  /** true si se detectó el patrón de coordenadas pegadas */
  esPegadas: boolean;
  /** Valor Y extraído (7 dígitos, empieza por 4 en Andalucía) */
  valorY: number | null;
  /** Valor X extraído (5-6 dígitos) */
  valorX: number | null;
  /** Nivel de confianza de la separación */
  confianza: NivelConfianza;
  /** Mensaje descriptivo del resultado */
  warning: string | null;
}

// ============================================================================
// FUNCIÓN: SEPARAR COORDENADAS CONCATENADAS
// ============================================================================

/**
 * Detecta y separa coordenadas X+Y concatenadas en un solo valor.
 * 
 * Intenta múltiples puntos de corte para encontrar una separación
 * que produzca valores dentro de los rangos UTM de Andalucía.
 * 
 * @param valor - Valor con coordenadas concatenadas (12+ dígitos)
 * @returns Resultado con X, Y separadas y nivel de confianza
 * 
 * @example
 * separarCoordenadasConcatenadas('5047504077905')
 * // { esConcatenado: true, valorX: 504750, valorY: 4077905, confianza: 'ALTA' }
 */
export function separarCoordenadasConcatenadas(valor: string): ResultadoConcatenacion {
  const resultado: ResultadoConcatenacion = {
    esConcatenado: false,
    valorX: null,
    valorY: null,
    confianza: 'BAJA',
    warning: null,
  };
  
  const soloDigitos = valor.replace(/[^\d]/g, '');
  
  if (soloDigitos.length < 12) {
    return resultado;
  }
  
  const mid = Math.floor(soloDigitos.length / 2);
  const puntosCorte = [mid, mid - 1, mid + 1, mid - 2, mid + 2];
  
  for (const corte of puntosCorte) {
    if (corte < 5 || corte > soloDigitos.length - 6) continue;
    
    const parte1 = parseInt(soloDigitos.substring(0, corte), 10);
    const parte2 = parseInt(soloDigitos.substring(corte), 10);
    
    const parte1EsX = parte1 >= RANGOS_ANDALUCIA.UTM.X_MIN && parte1 <= RANGOS_ANDALUCIA.UTM.X_MAX;
    const parte1EsY = parte1 >= RANGOS_ANDALUCIA.UTM.Y_MIN && parte1 <= RANGOS_ANDALUCIA.UTM.Y_MAX;
    const parte2EsX = parte2 >= RANGOS_ANDALUCIA.UTM.X_MIN && parte2 <= RANGOS_ANDALUCIA.UTM.X_MAX;
    const parte2EsY = parte2 >= RANGOS_ANDALUCIA.UTM.Y_MIN && parte2 <= RANGOS_ANDALUCIA.UTM.Y_MAX;
    
    // Caso ideal: X + Y en orden correcto
    if (parte1EsX && parte2EsY) {
      resultado.esConcatenado = true;
      resultado.valorX = parte1;
      resultado.valorY = parte2;
      resultado.confianza = 'ALTA';
      resultado.warning = `Coordenadas concatenadas separadas: ${valor} → X=${parte1}, Y=${parte2}`;
      return resultado;
    }
    
    // Caso invertido: Y + X
    if (parte1EsY && parte2EsX) {
      resultado.esConcatenado = true;
      resultado.valorX = parte2;
      resultado.valorY = parte1;
      resultado.confianza = 'MEDIA';
      resultado.warning = `Coordenadas concatenadas (orden invertido): ${valor} → X=${parte2}, Y=${parte1}`;
      return resultado;
    }
    
    // Caso ambiguo: ambos del mismo tipo
    if ((parte1EsX && parte2EsX) || (parte1EsY && parte2EsY)) {
      resultado.esConcatenado = true;
      resultado.valorX = parte1EsX ? parte1 : parte2;
      resultado.valorY = parte1EsY ? parte1 : parte2;
      resultado.confianza = 'BAJA';
      resultado.warning = `Coordenadas concatenadas (tipo ambiguo): ${valor} → ${parte1}, ${parte2}`;
      return resultado;
    }
  }
  
  return resultado;
}

// ============================================================================
// FUNCIÓN: SEPARAR COORDENADAS PEGADAS (BE-01)
// ============================================================================

/**
 * BE-01: Separa coordenadas Y+X fusionadas en un solo valor de 13-14 dígitos.
 * 
 * Formato esperado para Andalucía:
 * - 13 dígitos: YYYYYYYXXXXXX (Y=7 dígitos, X=6 dígitos)
 * - 14 dígitos: YYYYYYYYXXXXXX (Y con decimal implícito)
 * 
 * Rangos válidos Andalucía:
 * - Y UTM: 3.980.000 - 4.290.000 (7 dígitos empezando por 4)
 * - X UTM: 100.000 - 620.000 (5-6 dígitos)
 * 
 * @param valor - Valor a analizar (13-14 dígitos sin separadores)
 * @returns Resultado con Y, X separadas y validación de rangos
 * 
 * @example
 * separarCoordenadasPegadas('4077905504750')
 * // { esPegadas: true, valorY: 4077905, valorX: 504750, confianza: 'ALTA' }
 * 
 * separarCoordenadasPegadas('50475040779050')
 * // { esPegadas: true, valorY: 4077905, valorX: 504750, confianza: 'MEDIA' } (orden invertido)
 */
export function separarCoordenadasPegadas(valor: string): ResultadoCoordenadasPegadas {
  const resultado: ResultadoCoordenadasPegadas = {
    esPegadas: false,
    valorY: null,
    valorX: null,
    confianza: 'BAJA',
    warning: null,
  };
  
  if (!valor || typeof valor !== 'string') return resultado;
  
  // Extraer solo dígitos
  const soloDigitos = valor.replace(/[^\d]/g, '');
  const longitud = soloDigitos.length;
  
  // Solo procesar 13-14 dígitos
  if (longitud < 13 || longitud > 14) return resultado;
  
  let y: number;
  let x: number;
  
  if (longitud === 13) {
    // Formato: YYYYYYYXXXXXX (7 + 6 dígitos)
    // Y UTM Andalucía empieza por 4 (4.0-4.3 millones)
    y = parseInt(soloDigitos.slice(0, 7), 10);
    x = parseInt(soloDigitos.slice(7, 13), 10);
  } else {
    // 14 dígitos: YYYYYYYYXXXXXX (8 + 6 dígitos)
    // El dígito 8 podría ser decimal implícito
    y = parseInt(soloDigitos.slice(0, 7), 10);
    x = parseInt(soloDigitos.slice(7, 14), 10);
    
    // Si X tiene 7 dígitos, es posible que sea Y.decimal+X
    if (x > 999999) {
      // Reinterpretar: primeros 8 como Y (con decimal), últimos 6 como X
      const yConDecimal = parseInt(soloDigitos.slice(0, 8), 10) / 10;
      const xAlternativo = parseInt(soloDigitos.slice(8, 14), 10);
      
      // Verificar si esta interpretación es válida
      if (xAlternativo >= RANGOS_ANDALUCIA.UTM.X_MIN && 
          xAlternativo <= RANGOS_ANDALUCIA.UTM.X_MAX &&
          yConDecimal >= RANGOS_ANDALUCIA.UTM.Y_MIN && 
          yConDecimal <= RANGOS_ANDALUCIA.UTM.Y_MAX) {
        y = yConDecimal;
        x = xAlternativo;
      }
    }
  }
  
  // Validar rangos UTM Andalucía
  const yValido = y >= RANGOS_ANDALUCIA.UTM.Y_MIN && y <= RANGOS_ANDALUCIA.UTM.Y_MAX;
  const xValido = x >= RANGOS_ANDALUCIA.UTM.X_MIN && x <= RANGOS_ANDALUCIA.UTM.X_MAX;
  
  if (yValido && xValido) {
    resultado.esPegadas = true;
    resultado.valorY = y;
    resultado.valorX = x;
    resultado.confianza = 'ALTA';
    resultado.warning = `Coordenadas pegadas separadas: "${valor}" → Y=${y}, X=${x}`;
    return resultado;
  }
  
  // Intentar orden inverso: XXXXXXYYYYYYY
  if (longitud === 13) {
    const xInv = parseInt(soloDigitos.slice(0, 6), 10);
    const yInv = parseInt(soloDigitos.slice(6, 13), 10);
    
    const yInvValido = yInv >= RANGOS_ANDALUCIA.UTM.Y_MIN && yInv <= RANGOS_ANDALUCIA.UTM.Y_MAX;
    const xInvValido = xInv >= RANGOS_ANDALUCIA.UTM.X_MIN && xInv <= RANGOS_ANDALUCIA.UTM.X_MAX;
    
    if (yInvValido && xInvValido) {
      resultado.esPegadas = true;
      resultado.valorY = yInv;
      resultado.valorX = xInv;
      resultado.confianza = 'MEDIA';
      resultado.warning = `Coordenadas pegadas (orden invertido): "${valor}" → Y=${yInv}, X=${xInv}`;
      return resultado;
    }
  }
  
  // No se pudo separar válidamente
  if (!yValido || !xValido) {
    resultado.warning = `Coordenadas pegadas detectadas pero fuera de rango: Y=${y} (${yValido ? 'OK' : 'INVÁLIDO'}), X=${x} (${xValido ? 'OK' : 'INVÁLIDO'})`;
  }
  
  return resultado;
}
